import React, { useState, useEffect } from 'react';
import { useAuth } from '../../controllers/auth';
import { authService } from '../../services/authService';
import { adminService } from '../../services/adminService';
import { systemSettingsService } from '../../services/systemSettingsService';
import { neighborhoodService } from '../../services/neighborhoodService';
import { projectService } from '../../services/projectService';
import { leadService } from '../../services/leadService';
import { supabase } from '../../services/supabase';
import {
  User,
  Lock,
  Save,
  Key,
  Plus,
  Trash2,
  FileText,
  Building,
  Compass,
  Phone,
  Globe,
  Share2
} from 'lucide-react';

const DEFAULT_CONTACT_INFO = {
  support_phone: '9214901596',
  support_phone_display: '+91 92149 01596',
  website_url: 'https://nobrokerages.online',
  support_email: 'team@nobrokerages.online'
};

const formatPhoneDisplay = (rawPhone) => {
  const digits = String(rawPhone || '').replace(/\D/g, '');
  const local = digits.startsWith('91') && digits.length === 12 ? digits.slice(2) : digits;
  if (local.length === 10) return `+91 ${local.slice(0, 5)} ${local.slice(5)}`;
  if (local) return `+91 ${local}`;
  return DEFAULT_CONTACT_INFO.support_phone_display;
};

const normalizeWhatsAppPhone = (rawPhone) => {
  const digits = String(rawPhone || '').replace(/\D/g, '');
  if (digits.startsWith('91') && digits.length === 12) return digits.slice(2);
  return digits;
};

const defaultPropertyTypes = [
  'Apartment',
  'House / Villa',
  'Plot / Land',
  'Commercial',
  'PG / Co-living',
  'Warehouse / Godown',
  'Penthouse',
  'Shop / Showroom',
  'Office Space'
];

const Settings = () => {
  const { user, adminProfile, logAction } = useAuth();

  // Profile settings
  const [fullName, setFullName] = useState(adminProfile?.full_name || '');
  const [profileLoading, setProfileLoading] = useState(false);

  // Password reset settings
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Site Stats settings (Dynamic)
  const [siteStats, setSiteStats] = useState({
    cities_count: 'Serving 3 Cities',
    consultants_count: '100+ Property Consultants',
    sellers_count: '100+ Trusted Sellers',
    buyers_count: '1000+ Active Buyers'
  });
  const [statsLoading, setStatsLoading] = useState(false);

  // Office Branches settings (Dynamic)
  const [branches, setBranches] = useState([
    { id: 1, city: 'Ghazipur', title: 'Corporate Branch 1', address: 'Baxupur Ghazipur Uttar Pradesh Near Allahabad Bank' },
    { id: 2, city: 'Varanasi', title: 'Corporate Branch 2', address: 'Paharia Varanasi Uttarpradesh' },
    { id: 3, city: 'Lucknow', title: 'Corporate Branch 3', address: 'Gomtinagar Extension Lucknow' }
  ]);
  const [branchesLoading, setBranchesLoading] = useState(false);

  // Property Types settings (Dynamic)
  const [propertyTypes, setPropertyTypes] = useState(defaultPropertyTypes);
  const [typesLoading, setTypesLoading] = useState(false);
  const [newTypeInput, setNewTypeInput] = useState('');

  // Contact info (phone + website) used on the public website
  const [contactInfo, setContactInfo] = useState(DEFAULT_CONTACT_INFO);
  const [contactLoading, setContactLoading] = useState(false);

  // CRM & Lead Routing Configuration
  const [leadRouting, setLeadRouting] = useState({
    property_enquiry_routing: 'lister',
    popup_lead_routing: 'round_robin',
    popup_lead_staff_id: '',
    send_admin_copy: true,
    owner_reminder_enabled: true,
    owner_reminder_hours: 24
  });
  const [routingLoading, setRoutingLoading] = useState(false);
  const [salesStaffList, setSalesStaffList] = useState([]);

  // Launch Offer Configuration (Requirement #5)
  const [launchOffer, setLaunchOffer] = useState({
    offer_active: true,
    offer_limit: 500,
    total_claimed: 10
  });
  const [offerLoading, setOfferLoading] = useState(false);

  // Legal & Content Documents
  const [aboutUsText, setAboutUsText] = useState('');
  const [termsText, setTermsText] = useState('');
  const [privacyText, setPrivacyText] = useState('');
  const [legalLoading, setLegalLoading] = useState(false);

  // Trending Neighborhoods
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [newNeighName, setNewNeighName] = useState('');
  const [newNeighCity, setNewNeighCity] = useState('Varanasi');
  const [newNeighGrowth, setNewNeighGrowth] = useState('+12.5%');

  // New Projects
  const [projects, setProjects] = useState([]);
  const [newProjName, setNewProjName] = useState('');
  const [newProjBuilder, setNewProjBuilder] = useState('');
  const [newProjCity, setNewProjCity] = useState('Lucknow');
  const [newProjPrice, setNewProjPrice] = useState('₹45 Lakhs Onwards');

  useEffect(() => {
    fetchSystemSettings();
  }, []);

  const fetchSystemSettings = async () => {
    try {
      const { data: statsData } = await systemSettingsService.getSetting('site_stats');
      if (statsData?.value) setSiteStats(statsData.value);

      const { data: branchData } = await systemSettingsService.getSetting('office_branches');
      if (branchData?.value && Array.isArray(branchData.value)) setBranches(branchData.value);

      const { data: typeData } = await systemSettingsService.getSetting('property_types');
      if (typeData?.value && Array.isArray(typeData.value)) setPropertyTypes(typeData.value);

      const { data: contactData } = await systemSettingsService.getSetting('contact_info');
      if (contactData?.value && typeof contactData.value === 'object') {
        setContactInfo({ ...DEFAULT_CONTACT_INFO, ...contactData.value });
      }

      const [routingRes, staffRes] = await Promise.all([
        leadService.getLeadRoutingSettings(),
        leadService.getAdmins()
      ]);
      if (routingRes?.data) {
        setLeadRouting(prev => ({
          ...prev,
          ...routingRes.data,
          property_enquiry_routing: routingRes.data.property_enquiry_routing || routingRes.data.route_to || 'lister',
          popup_lead_routing: routingRes.data.popup_lead_routing || routingRes.data.popup_route_to || 'round_robin',
          popup_lead_staff_id: routingRes.data.popup_lead_staff_id || ''
        }));
      }
      if (staffRes?.data) {
        setSalesStaffList((staffRes.data || []).filter(s => s.status === 'Active'));
      }

      // Fetch Launch Offer & Direct Contact Counts
      const { data: offerData } = await systemSettingsService.getSetting('launch_offer');
      const { count: directCount } = await supabase
        .from('properties')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'approved')
        .eq('direct_contact', true);

      if (offerData?.value && typeof offerData.value === 'object') {
        setLaunchOffer({
          offer_active: offerData.value.offer_active !== false,
          offer_limit: Number(offerData.value.offer_limit) || 500,
          total_claimed: directCount || 0
        });
      } else {
        setLaunchOffer(prev => ({ ...prev, total_claimed: directCount || 0 }));
      }

      const { data: aboutData } = await systemSettingsService.getSetting('about_us');
      if (aboutData?.value) setAboutUsText(aboutData.value);

      const { data: termsData } = await systemSettingsService.getSetting('terms_conditions');
      if (termsData?.value) setTermsText(termsData.value);

      const { data: privacyData } = await systemSettingsService.getSetting('privacy_policy');
      if (privacyData?.value) setPrivacyText(privacyData.value);

      const { data: neighData } = await neighborhoodService.getNeighborhoods();
      if (neighData) setNeighborhoods(neighData);

      const { data: projData } = await projectService.getProjects();
      if (projData) setProjects(projData);
    } catch (err) {
      console.error('Error fetching system settings:', err);
    }
  };

  const handleAddNeighborhood = async (e) => {
    e.preventDefault();
    if (!newNeighName.trim()) return;
    const { data, error } = await neighborhoodService.createNeighborhood({
      name: newNeighName.trim(),
      city: newNeighCity,
      price_growth: newNeighGrowth,
      image_url: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=600&q=80'
    });
    if (!error && data) {
      setNeighborhoods([...neighborhoods, ...data]);
      setNewNeighName('');
      alert('Neighborhood added!');
    }
  };

  const handleDeleteNeighborhood = async (id) => {
    const { error } = await neighborhoodService.deleteNeighborhood(id);
    if (!error) {
      setNeighborhoods(neighborhoods.filter(n => n.id !== id));
    }
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    if (!newProjName.trim() || !newProjBuilder.trim()) return;
    const { data, error } = await projectService.createProject({
      project_name: newProjName.trim(),
      builder_name: newProjBuilder.trim(),
      city: newProjCity,
      starting_price: newProjPrice,
      image_url: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?auto=format&fit=crop&w=600&q=80'
    });
    if (!error && data) {
      setProjects([...projects, ...data]);
      setNewProjName('');
      setNewProjBuilder('');
      alert('New Project added!');
    }
  };

  const handleDeleteProject = async (id) => {
    const { error } = await projectService.deleteProject(id);
    if (!error) {
      setProjects(projects.filter(p => p.id !== id));
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) return;
    setProfileLoading(true);
    try {
      const { error } = await adminService.updateAdminProfile(user?.id, fullName.trim());
      if (error) throw error;
      alert('Profile updated successfully!');
    } catch (err) {
      alert(err.message || 'Error updating profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    setPasswordLoading(true);
    try {
      const { error } = await authService.updatePassword(newPassword);
      if (error) throw error;
      setNewPassword('');
      setConfirmPassword('');
      alert('Password updated successfully!');
    } catch (err) {
      alert(err.message || 'Error updating password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSaveContactInfo = async (e) => {
    e.preventDefault();
    const phone = normalizeWhatsAppPhone(contactInfo.support_phone);
    if (!phone || phone.length < 10) {
      alert('Please enter a valid 10-digit mobile number.');
      return;
    }
    const websiteUrl = (contactInfo.website_url || '').trim().replace(/\/$/, '');
    if (!websiteUrl || !/^https?:\/\//i.test(websiteUrl)) {
      alert('Please enter a valid website URL starting with http:// or https://');
      return;
    }

    setContactLoading(true);
    try {
      const payload = {
        ...contactInfo,
        support_phone: phone,
        support_phone_display: formatPhoneDisplay(phone),
        website_url: websiteUrl,
        support_email: (contactInfo.support_email || DEFAULT_CONTACT_INFO.support_email).trim()
      };
      const { error } = await systemSettingsService.updateSetting('contact_info', payload);
      if (error) throw error;
      setContactInfo(payload);
      alert('Contact number & website updated. Changes are live on the website.');
    } catch (err) {
      alert(err.message || 'Error saving contact info');
    } finally {
      setContactLoading(false);
    }
  };

  const handleSaveLeadRouting = async (e) => {
    e.preventDefault();
    setRoutingLoading(true);
    try {
      const payload = {
        property_enquiry_routing: leadRouting.property_enquiry_routing,
        popup_lead_routing: leadRouting.popup_lead_routing,
        popup_lead_staff_id: leadRouting.popup_lead_routing === 'specific_staff' ? leadRouting.popup_lead_staff_id : null,
        route_to: leadRouting.property_enquiry_routing,
        route_property_enquiry_to_owner: leadRouting.property_enquiry_routing === 'lister',
        popup_route_to: leadRouting.popup_lead_routing,
        send_admin_copy: !!leadRouting.send_admin_copy,
        owner_reminder_enabled: !!leadRouting.owner_reminder_enabled,
        owner_reminder_hours: Number(leadRouting.owner_reminder_hours) || 24
      };

      const { error } = await leadService.updateLeadRoutingSettings(payload);
      if (error) throw error;

      await logAction(
        user?.id,
        adminProfile?.full_name || 'System',
        'Update Lead Routing',
        'crm_settings',
        `Property leads -> ${leadRouting.property_enquiry_routing}, Popup leads -> ${leadRouting.popup_lead_routing}`
      );
      alert('Lead routing preferences updated successfully!');
    } catch (err) {
      alert(err.message || 'Error saving lead routing setting');
    } finally {
      setRoutingLoading(false);
    }
  };

  const handleSaveLaunchOffer = async (e) => {
    e.preventDefault();
    setOfferLoading(true);
    try {
      await systemSettingsService.updateSetting('launch_offer', {
        offer_active: Boolean(launchOffer.offer_active),
        offer_limit: Number(launchOffer.offer_limit) || 500,
        title: 'Direct Contact Launch Offer (First 500 Listings)',
        updated_at: new Date().toISOString()
      });

      await logAction(
        user?.id,
        adminProfile?.full_name || 'System',
        'Update Launch Offer',
        'system_settings',
        `Offer Active: ${launchOffer.offer_active ? 'ON' : 'OFF'}, Limit: ${launchOffer.offer_limit}`
      );

      alert('Launch Offer configuration saved successfully!');
    } catch (err) {
      alert(err.message || 'Error saving launch offer setting');
    } finally {
      setOfferLoading(false);
    }
  };

  const handleSaveLegalSettings = async (e) => {
    e.preventDefault();
    setLegalLoading(true);
    try {
      await systemSettingsService.updateSetting('about_us', aboutUsText);
      await systemSettingsService.updateSetting('terms_conditions', termsText);
      await systemSettingsService.updateSetting('privacy_policy', privacyText);
      alert('Legal & About Us content updated live!');
    } catch (err) {
      alert(err.message || 'Error saving legal content');
    } finally {
      setLegalLoading(false);
    }
  };

  return (
    <div className="page-body" style={{ maxWidth: '1100px', margin: '0 auto', padding: '24px' }}>
      <h1 className="page-title">Console & Platform Settings</h1>
      <p className="page-subtitle">Manage contact number, website URL, trending localities, new projects, legal documents, and security.</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '24px' }}>

        {/* Left Column: Contact, Trending Neighborhoods, New Projects, Legal */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

          {/* CRM & Lead Routing Control Card */}
          <div className="card">
            <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--primary)' }}>
              <Share2 size={18} /> CRM & Lead Routing Control
            </h3>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px', lineHeight: 1.5 }}>
              Configure how property enquiries and website popup consultation leads are distributed across staff or listers.
            </p>
            <form onSubmit={handleSaveLeadRouting} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>

              {/* Section 1: Property Page Enquiry Routing */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>
                  1. Property Page Enquiry Routing
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    padding: '12px',
                    borderRadius: 'var(--radius-sm)',
                    border: leadRouting.property_enquiry_routing === 'lister' ? '2px solid var(--primary)' : '1px solid var(--border-primary)',
                    backgroundColor: leadRouting.property_enquiry_routing === 'lister' ? 'var(--bg-tertiary)' : 'transparent',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="radio"
                      name="propertyEnquiryRouting"
                      value="lister"
                      checked={leadRouting.property_enquiry_routing === 'lister'}
                      onChange={() => setLeadRouting({ ...leadRouting, property_enquiry_routing: 'lister' })}
                      style={{ marginTop: '3px' }}
                    />
                    <div>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', display: 'block' }}>
                        Direct to Property Lister / Owner (Active)
                      </span>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginTop: '2px', lineHeight: 1.4 }}>
                        Enquiries are delivered straight to the user who posted the listing. If a listing has no owner, it automatically falls back to Platform Admin.
                      </span>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    padding: '12px',
                    borderRadius: 'var(--radius-sm)',
                    border: leadRouting.property_enquiry_routing === 'admin' ? '2px solid var(--primary)' : '1px solid var(--border-primary)',
                    backgroundColor: leadRouting.property_enquiry_routing === 'admin' ? 'var(--bg-tertiary)' : 'transparent',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="radio"
                      name="propertyEnquiryRouting"
                      value="admin"
                      checked={leadRouting.property_enquiry_routing === 'admin'}
                      onChange={() => setLeadRouting({ ...leadRouting, property_enquiry_routing: 'admin' })}
                      style={{ marginTop: '3px' }}
                    />
                    <div>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', display: 'block' }}>
                        Route all Property Enquiries to Platform Admin
                      </span>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginTop: '2px', lineHeight: 1.4 }}>
                        All property page inquiries are collected centrally by the admin team. Listers will not receive leads directly.
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Section 2: Website Popup Leads Routing */}
              <div style={{ borderTop: '1px solid var(--border-primary)', paddingTop: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>
                  2. Website Popup Consultation Leads Routing
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    padding: '12px',
                    borderRadius: 'var(--radius-sm)',
                    border: leadRouting.popup_lead_routing === 'round_robin' ? '2px solid var(--primary)' : '1px solid var(--border-primary)',
                    backgroundColor: leadRouting.popup_lead_routing === 'round_robin' ? 'var(--bg-tertiary)' : 'transparent',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="radio"
                      name="popupLeadRouting"
                      value="round_robin"
                      checked={leadRouting.popup_lead_routing === 'round_robin'}
                      onChange={() => setLeadRouting({ ...leadRouting, popup_lead_routing: 'round_robin' })}
                      style={{ marginTop: '3px' }}
                    />
                    <div>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', display: 'block' }}>
                        Auto Round-Robin Distribution (Recommended)
                      </span>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginTop: '2px', lineHeight: 1.4 }}>
                        New popup leads are automatically distributed equally among all active Sales Executives and Staff members.
                      </span>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    padding: '12px',
                    borderRadius: 'var(--radius-sm)',
                    border: leadRouting.popup_lead_routing === 'specific_staff' ? '2px solid var(--primary)' : '1px solid var(--border-primary)',
                    backgroundColor: leadRouting.popup_lead_routing === 'specific_staff' ? 'var(--bg-tertiary)' : 'transparent',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="radio"
                      name="popupLeadRouting"
                      value="specific_staff"
                      checked={leadRouting.popup_lead_routing === 'specific_staff'}
                      onChange={() => setLeadRouting({ ...leadRouting, popup_lead_routing: 'specific_staff' })}
                      style={{ marginTop: '3px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', display: 'block' }}>
                        Route to a Dedicated Sales Executive
                      </span>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginTop: '2px', marginBottom: '8px', lineHeight: 1.4 }}>
                        All popup leads will be assigned directly to the selected team member.
                      </span>

                      {leadRouting.popup_lead_routing === 'specific_staff' && (
                        <select
                          className="filter-select"
                          style={{ width: '100%', fontSize: '12px', marginTop: '4px' }}
                          value={leadRouting.popup_lead_staff_id || ''}
                          onChange={(e) => setLeadRouting({ ...leadRouting, popup_lead_staff_id: e.target.value })}
                          required
                        >
                          <option value="">-- Select Dedicated Staff Member --</option>
                          {salesStaffList.map(s => (
                            <option key={s.id} value={s.id}>{s.full_name} ({s.role})</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    padding: '12px',
                    borderRadius: 'var(--radius-sm)',
                    border: leadRouting.popup_lead_routing === 'unassigned' ? '2px solid var(--primary)' : '1px solid var(--border-primary)',
                    backgroundColor: leadRouting.popup_lead_routing === 'unassigned' ? 'var(--bg-tertiary)' : 'transparent',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="radio"
                      name="popupLeadRouting"
                      value="unassigned"
                      checked={leadRouting.popup_lead_routing === 'unassigned'}
                      onChange={() => setLeadRouting({ ...leadRouting, popup_lead_routing: 'unassigned' })}
                      style={{ marginTop: '3px' }}
                    />
                    <div>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', display: 'block' }}>
                        Central Unassigned Pool
                      </span>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginTop: '2px', lineHeight: 1.4 }}>
                        Leads are stored without assignment in the central leads pool. Any sales executive can claim or be manually assigned.
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Section 3: Oversight, Admin Copy & Unattended Reminders (Rules 6, 8, 11) */}
              <div style={{ borderTop: '1px solid var(--border-primary)', paddingTop: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>
                  3. Admin Copy & Owner Follow-up Reminders
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    padding: '12px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border-primary)',
                    backgroundColor: leadRouting.send_admin_copy ? 'var(--bg-tertiary)' : 'transparent',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={!!leadRouting.send_admin_copy}
                      onChange={(e) => setLeadRouting({ ...leadRouting, send_admin_copy: e.target.checked })}
                      style={{ marginTop: '3px' }}
                    />
                    <div>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', display: 'block' }}>
                        Admin ko copy bhejo (Real-time Oversight)
                      </span>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginTop: '2px', lineHeight: 1.4 }}>
                        Even when property enquiries go directly to owners, an active copy is preserved in Admin CRM Central Leads for quality monitoring.
                      </span>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    padding: '12px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border-primary)',
                    backgroundColor: leadRouting.owner_reminder_enabled ? 'var(--bg-tertiary)' : 'transparent',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={!!leadRouting.owner_reminder_enabled}
                      onChange={(e) => setLeadRouting({ ...leadRouting, owner_reminder_enabled: e.target.checked })}
                      style={{ marginTop: '3px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', display: 'block' }}>
                        Unattended Lead Reminder to Property Owners
                      </span>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginTop: '2px', marginBottom: '8px', lineHeight: 1.4 }}>
                        Automatically triggers an urgent notification to property owners if a new lead remains in "New" status without contact.
                      </span>

                      {leadRouting.owner_reminder_enabled && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                          <span style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: 500 }}>
                            Send reminder after:
                          </span>
                          <input
                            type="number"
                            min="1"
                            max="72"
                            value={leadRouting.owner_reminder_hours || 24}
                            onChange={(e) => setLeadRouting({ ...leadRouting, owner_reminder_hours: e.target.value })}
                            className="search-input"
                            style={{ width: '80px', height: '32px', textAlign: 'center', fontWeight: 700 }}
                          />
                          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>hours (default: 24h)</span>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                <button type="submit" disabled={routingLoading} className="btn btn-primary" style={{ gap: '6px' }}>
                  <Save size={15} /> {routingLoading ? 'Saving...' : 'Save Lead Routing Preferences'}
                </button>
              </div>
            </form>
          </div>

          {/* Direct Contact Launch Offer (First 500 Listings) */}
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: '8px', color: '#15803D' }}>
                🎉 Direct Contact Launch Offer (First 500 Listings)
              </h3>
              <span className="badge" style={{
                backgroundColor: launchOffer.offer_active ? 'rgba(37, 211, 102, 0.12)' : 'var(--bg-tertiary)',
                color: launchOffer.offer_active ? '#15803D' : 'var(--text-secondary)',
                border: launchOffer.offer_active ? '1px solid rgba(37, 211, 102, 0.4)' : '1px solid var(--border-primary)',
                fontWeight: 700
              }}>
                {launchOffer.offer_active ? 'Offer Active' : 'Offer Paused'}
              </span>
            </div>

            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px', lineHeight: 1.5 }}>
              Approved properties under the limit route buyer WhatsApp chats and enquiries directly to the property lister. Beyond this limit (or when paused), leads route to Super Admin.
            </p>

            {/* Slots Claimed Progress Bar */}
            <div style={{
              background: 'var(--bg-tertiary)',
              border: '1px solid var(--border-primary)',
              borderRadius: 'var(--radius-md)',
              padding: '14px 16px',
              marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', fontSize: '13px' }}>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                  Slots Allocation Status:
                </span>
                <span style={{ fontWeight: 700, color: 'var(--primary)' }}>
                  {launchOffer.total_claimed} / {launchOffer.offer_limit} Claimed ({Math.max(0, launchOffer.offer_limit - launchOffer.total_claimed)} Slots Left)
                </span>
              </div>
              <div style={{ width: '100%', height: '8px', backgroundColor: 'var(--border-secondary)', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{
                  width: `${Math.min(100, Math.round((launchOffer.total_claimed / (launchOffer.offer_limit || 1)) * 100))}%`,
                  height: '100%',
                  backgroundColor: '#25D366',
                  transition: 'width 0.4s ease'
                }} />
              </div>
            </div>

            <form onSubmit={handleSaveLaunchOffer} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
                {/* Active Toggle */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-primary)',
                  backgroundColor: 'var(--bg-secondary)'
                }}>
                  <input
                    type="checkbox"
                    id="launchOfferActive"
                    checked={Boolean(launchOffer.offer_active)}
                    onChange={(e) => setLaunchOffer({ ...launchOffer, offer_active: e.target.checked })}
                    style={{ width: '16px', height: '16px', accentColor: '#25D366', cursor: 'pointer' }}
                  />
                  <label htmlFor="launchOfferActive" style={{ cursor: 'pointer', flex: 1 }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', display: 'block' }}>
                      Launch Offer Enabled
                    </span>
                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                      New approved listings receive direct lister routing.
                    </span>
                  </label>
                </div>

                {/* Offer Limit */}
                <div style={{
                  padding: '12px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-primary)',
                  backgroundColor: 'var(--bg-secondary)'
                }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    Offer Listings Limit
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10000"
                    value={launchOffer.offer_limit || 500}
                    onChange={(e) => setLaunchOffer({ ...launchOffer, offer_limit: parseInt(e.target.value) || 500 })}
                    className="search-input"
                    style={{ width: '100%', height: '36px', fontWeight: 700 }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                <button type="submit" disabled={offerLoading} className="btn btn-primary" style={{ gap: '6px' }}>
                  <Save size={15} /> {offerLoading ? 'Saving...' : 'Save Launch Offer Settings'}
                </button>
              </div>
            </form>
          </div>

          {/* Contact Number & Website */}
          <div className="card">
            <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--primary)' }}>
              <Phone size={18} /> Website Contact & WhatsApp
            </h3>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px', lineHeight: 1.5 }}>
              Change the query / WhatsApp number and website URL shown across the public website. No code change needed.
            </p>
            <form onSubmit={handleSaveContactInfo} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  Mobile / WhatsApp Number
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={contactInfo.support_phone}
                  onChange={(e) => setContactInfo({
                    ...contactInfo,
                    support_phone: e.target.value.replace(/[^\d]/g, '').slice(0, 10)
                  })}
                  placeholder="e.g. 9214901596"
                  style={{ width: '100%', padding: '8px 12px' }}
                  required
                />
                <small style={{ display: 'block', marginTop: '6px', fontSize: '11px', color: 'var(--text-muted)' }}>
                  Preview: {formatPhoneDisplay(contactInfo.support_phone)} · WhatsApp: wa.me/{normalizeWhatsAppPhone(contactInfo.support_phone) || '…'}
                </small>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><Globe size={12} /> Website URL</span>
                </label>
                <input
                  type="url"
                  value={contactInfo.website_url}
                  onChange={(e) => setContactInfo({ ...contactInfo, website_url: e.target.value })}
                  placeholder="https://nobrokerages.online"
                  style={{ width: '100%', padding: '8px 12px' }}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  Support Email
                </label>
                <input
                  type="email"
                  value={contactInfo.support_email}
                  onChange={(e) => setContactInfo({ ...contactInfo, support_email: e.target.value })}
                  placeholder="team@nobrokerages.online"
                  style={{ width: '100%', padding: '8px 12px' }}
                />
              </div>

              <button
                type="submit"
                disabled={contactLoading}
                className="btn btn-primary btn-sm"
                style={{ alignSelf: 'flex-start', gap: '6px' }}
              >
                <Save size={14} /> {contactLoading ? 'Saving...' : 'Save Contact & Website'}
              </button>
            </form>
          </div>

          {/* Trending Neighborhoods Manager */}
          <div className="card">
            <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--primary)' }}>
              <Compass size={18} /> Trending Neighborhoods & Localities
            </h3>
            <form onSubmit={handleAddNeighborhood} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '10px', marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="Locality (e.g. Assi Ghat)"
                value={newNeighName}
                onChange={(e) => setNewNeighName(e.target.value)}
                style={{ padding: '8px 10px', fontSize: '12px' }}
                required
              />
              <select
                value={newNeighCity}
                onChange={(e) => setNewNeighCity(e.target.value)}
                style={{ padding: '8px 10px', fontSize: '12px' }}
              >
                <option value="Ghazipur">Ghazipur</option>
                <option value="Varanasi">Varanasi</option>
                <option value="Lucknow">Lucknow</option>
              </select>
              <input
                type="text"
                placeholder="Growth (e.g. +15.1%)"
                value={newNeighGrowth}
                onChange={(e) => setNewNeighGrowth(e.target.value)}
                style={{ padding: '8px 10px', fontSize: '12px' }}
              />
              <button type="submit" className="btn btn-primary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Plus size={14} /> Add
              </button>
            </form>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {neighborhoods.map((n) => (
                <div key={n.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', backgroundColor: 'var(--bg-tertiary)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-primary)' }}>
                  <div>
                    <strong style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{n.name}</strong>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '8px' }}>({n.city} • {n.price_growth || '+12.5%'})</span>
                  </div>
                  <button type="button" onClick={() => handleDeleteNeighborhood(n.id)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer' }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* New Projects Manager */}
          <div className="card">
            <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--primary)' }}>
              <Building size={18} /> Builder New Projects
            </h3>
            <form onSubmit={handleAddProject} style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <input
                  type="text"
                  placeholder="Project Name (e.g. Skyline Towers)"
                  value={newProjName}
                  onChange={(e) => setNewProjName(e.target.value)}
                  style={{ padding: '8px 10px', fontSize: '12px' }}
                  required
                />
                <input
                  type="text"
                  placeholder="Builder Name (e.g. Apex Group)"
                  value={newProjBuilder}
                  onChange={(e) => setNewProjBuilder(e.target.value)}
                  style={{ padding: '8px 10px', fontSize: '12px' }}
                  required
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '10px' }}>
                <select
                  value={newProjCity}
                  onChange={(e) => setNewProjCity(e.target.value)}
                  style={{ padding: '8px 10px', fontSize: '12px' }}
                >
                  <option value="Ghazipur">Ghazipur</option>
                  <option value="Varanasi">Varanasi</option>
                  <option value="Lucknow">Lucknow</option>
                </select>
                <input
                  type="text"
                  placeholder="Price (e.g. ₹45L Onwards)"
                  value={newProjPrice}
                  onChange={(e) => setNewProjPrice(e.target.value)}
                  style={{ padding: '8px 10px', fontSize: '12px' }}
                />
                <button type="submit" className="btn btn-primary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Plus size={14} /> Add Project
                </button>
              </div>
            </form>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {projects.map((p) => (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', backgroundColor: 'var(--bg-tertiary)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-primary)' }}>
                  <div>
                    <strong style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{p.project_name}</strong>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '8px' }}>by {p.builder_name} ({p.city} • {p.starting_price})</span>
                  </div>
                  <button type="button" onClick={() => handleDeleteProject(p.id)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer' }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Legal Documents & About Us Editor Card */}
          <div className="card">
            <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--primary)' }}>
              <FileText size={18} /> Legal Pages & About Us (Mobile App & Web)
            </h3>
            <form onSubmit={handleSaveLegalSettings} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  About Us Overview Text
                </label>
                <textarea
                  rows={3}
                  value={aboutUsText}
                  onChange={(e) => setAboutUsText(e.target.value)}
                  placeholder="Company overview, vision, and mission..."
                  style={{ width: '100%', padding: '8px 12px', resize: 'vertical' }}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  Terms & Conditions Text
                </label>
                <textarea
                  rows={4}
                  value={termsText}
                  onChange={(e) => setTermsText(e.target.value)}
                  placeholder="Full terms and conditions policy content..."
                  style={{ width: '100%', padding: '8px 12px', resize: 'vertical' }}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  Privacy Policy Text
                </label>
                <textarea
                  rows={4}
                  value={privacyText}
                  onChange={(e) => setPrivacyText(e.target.value)}
                  placeholder="Full privacy policy document content..."
                  style={{ width: '100%', padding: '8px 12px', resize: 'vertical' }}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={legalLoading}
                className="btn btn-primary btn-sm"
                style={{ alignSelf: 'flex-start', gap: '6px', marginTop: '6px' }}
              >
                <Save size={14} /> {legalLoading ? 'Saving...' : 'Save Legal & About Content'}
              </button>
            </form>
          </div>

        </div>

        {/* Right Column: Profile & Security */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

          {/* Admin Profile Card */}
          <div className="card">
            <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <User size={16} color="var(--primary)" /> Administrator Profile
            </h3>
            <form onSubmit={handleUpdateProfile} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  Email Address
                </label>
                <input
                  type="text"
                  value={adminProfile?.email || ''}
                  disabled
                  style={{ backgroundColor: 'var(--bg-tertiary)', cursor: 'not-allowed', width: '100%', padding: '8px 12px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  Full Name / Display Name
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px' }}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={profileLoading}
                className="btn btn-primary btn-sm"
                style={{ alignSelf: 'flex-start', gap: '6px' }}
              >
                <Save size={14} /> {profileLoading ? 'Saving...' : 'Update Profile'}
              </button>
            </form>
          </div>

          {/* Password Card */}
          <div className="card">
            <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Lock size={16} color="var(--primary)" /> Change Password
            </h3>
            <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  required
                  style={{ width: '100%', padding: '8px 12px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-type new password"
                  required
                  style={{ width: '100%', padding: '8px 12px' }}
                />
              </div>

              <button
                type="submit"
                disabled={passwordLoading}
                className="btn btn-primary btn-sm"
                style={{ alignSelf: 'flex-start', gap: '6px' }}
              >
                <Key size={14} /> {passwordLoading ? 'Resetting...' : 'Change Password'}
              </button>
            </form>
          </div>

        </div>

      </div>
    </div>
  );
};

export default Settings;
